import { Link } from "react-router-dom";
import { STUDIO } from "../data/constants";

/**
 * Production footer — lives on every page below the auth wall. Designed to
 * match the apple.com / linear.app cadence: dim heading rows, brand blurb
 * top-left, three or four columns of links, a stats strip, and a thin
 * legal bar.
 *
 * Edit `BRAND_DETAILS` to update business contact info; the rest is
 * structural.
 */
export function Footer() {
  return (
    <footer style={{
      borderTop: "1px solid var(--border)",
      background: "var(--bg-soft)",
      paddingTop: 56,
      paddingBottom: 28,
      marginTop: 64,
    }}>
      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 24px" }}>

        {/* Stats strip — quick visual proof the studio is real and busy */}
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: 4,
          marginBottom: 48,
          paddingBottom: 40,
          borderBottom: "1px solid var(--border)",
          textAlign: "center",
        }}>
          <Stat value="2,400+" label="Designs generated" />
          <Stat value="14"      label="Studio technicians" />
          <Stat value="98%"     label="On-time bookings" />
          <Stat value="4.9 ★"   label="Average review" />
        </div>

        {/* Main column grid */}
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
          gap: 48,
          marginBottom: 44,
        }}>
          {/* Brand block */}
          <div>
            <div style={{
              display: "inline-flex", alignItems: "center", gap: 10,
              marginBottom: 14,
            }}>
              <span aria-hidden="true" style={{
                width: 32, height: 32, borderRadius: 12,
                background: "var(--bg-chip)",
                border: "1px solid var(--border)",
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                overflow: "hidden",
              }}>
                <img
                  src="/app-icons/oge-app-icon-photoreal.png"
                  alt=""
                  style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                />
              </span>
              <span style={{
                fontFamily: "var(--font-display)",
                fontSize: 17, fontWeight: 800, color: "var(--text)",
                letterSpacing: 0,
              }}>{STUDIO}</span>
            </div>
            <p style={{
              fontSize: "clamp(14px, 3.5vw, 15px)",
              color: "var(--text-muted)",
              lineHeight: 1.7,
              maxWidth: 320,
            }}>
              AI-crafted bespoke nail design. Six photoreal previews on a hand
              that matches your skin tone, then a tap-to-book appointment with
              a vetted technician — designed in Lagos, served everywhere.
            </p>
            <div style={{ marginTop: 14, display: "flex", gap: 10 }}>
              <SocialIcon href="https://instagram.com/ogeeyesthetics" label="Instagram" path="M12 2.16c3.2 0 3.58.01 4.85.07 1.17.05 1.8.25 2.23.41.56.22.96.48 1.38.9.42.42.68.82.9 1.38.16.42.36 1.06.41 2.23.06 1.27.07 1.65.07 4.85s-.01 3.58-.07 4.85c-.05 1.17-.25 1.8-.41 2.23-.22.56-.48.96-.9 1.38-.42.42-.82.68-1.38.9-.42.16-1.06.36-2.23.41-1.27.06-1.65.07-4.85.07s-3.58-.01-4.85-.07c-1.17-.05-1.8-.25-2.23-.41a3.75 3.75 0 01-1.38-.9 3.75 3.75 0 01-.9-1.38c-.16-.42-.36-1.06-.41-2.23C2.17 15.58 2.16 15.2 2.16 12s.01-3.58.07-4.85c.05-1.17.25-1.8.41-2.23.22-.56.48-.96.9-1.38.42-.42.82-.68 1.38-.9.42-.16 1.06-.36 2.23-.41C8.42 2.17 8.8 2.16 12 2.16zm0 1.62c-3.15 0-3.5.01-4.74.07-1.05.05-1.62.22-2 .37-.5.2-.86.43-1.24.81-.38.38-.61.74-.81 1.24-.15.38-.32.95-.37 2-.06 1.24-.07 1.59-.07 4.74s.01 3.5.07 4.74c.05 1.05.22 1.62.37 2 .2.5.43.86.81 1.24.38.38.74.61 1.24.81.38.15.95.32 2 .37 1.24.06 1.59.07 4.74.07s3.5-.01 4.74-.07c1.05-.05 1.62-.22 2-.37.5-.2.86-.43 1.24-.81.38-.38.61-.74.81-1.24.15-.38.32-.95.37-2 .06-1.24.07-1.59.07-4.74s-.01-3.5-.07-4.74c-.05-1.05-.22-1.62-.37-2-.2-.5-.43-.86-.81-1.24a3.34 3.34 0 00-1.24-.81c-.38-.15-.95-.32-2-.37-1.24-.06-1.59-.07-4.74-.07zm0 2.76a5.46 5.46 0 110 10.92 5.46 5.46 0 010-10.92zm0 1.62a3.84 3.84 0 100 7.68 3.84 3.84 0 000-7.68zm5.66-2.97a1.27 1.27 0 110 2.55 1.27 1.27 0 010-2.55z" />
              <SocialIcon href="https://twitter.com/ogeeyesthetics" label="X / Twitter" path="M17.5 3h2.92l-6.38 7.3 7.5 9.7h-5.88l-4.6-6.02L5.86 20H2.93l6.83-7.81L2.55 3h6.03l4.16 5.5L17.5 3zm-1.03 14.46h1.62L7.6 4.46H5.86l10.6 13z" />
              <SocialIcon href="https://tiktok.com/@ogeeyesthetics" label="TikTok" path="M19.5 6.69a4.84 4.84 0 01-2.85-.93v7.92a5.79 5.79 0 11-5.79-5.79c.21 0 .42.01.62.04v2.6a3.2 3.2 0 103.21 3.2V2h2.55a4.84 4.84 0 002.26 4.16v.53z" />
            </div>
          </div>

          <FooterColumn title="Studio" links={[
            { to: "/",        label: "Home" },
            { to: "/book",    label: "Book a design" },
            { to: "/gallery", label: "Gallery" },
            { to: "/history", label: "Your history" },
          ]} />

          <FooterColumn title="For technicians" links={[
            { to: "/login",   label: "Sign in" },
            { to: "/tech",    label: "Studio dashboard" },
            { to: "/admin",   label: "Admin console" },
            { href: "mailto:work@ogeeyesthetics.com", label: "Join the studio" },
          ]} />

          <FooterColumn title="Help" links={[
            { href: `mailto:${BRAND_DETAILS.email}`, label: BRAND_DETAILS.email },
            { href: `tel:${BRAND_DETAILS.phoneTel}`, label: BRAND_DETAILS.phoneDisplay },
            { to: "/legal/privacy", label: "Privacy policy" },
            { to: "/legal/terms",   label: "Terms of use" },
          ]} />
        </div>

        {/* Legal bar */}
        <div style={{
          paddingTop: 24, borderTop: "1px solid var(--border)",
          display: "flex", flexWrap: "wrap", gap: 14, alignItems: "center",
          justifyContent: "center", flexDirection: "column",
          fontSize: "clamp(13px, 3vw, 14px)",
          color: "var(--text-muted)",
        }}>
          <div style={{ textAlign: "center", textWrap: "balance", maxWidth: 340 }}>
            © {new Date().getFullYear()} {BRAND_DETAILS.legalName}. All rights reserved.
          </div>
          <div style={{ display: "flex", gap: 14, flexWrap: "wrap", justifyContent: "center" }}>
            <span>{BRAND_DETAILS.address}</span>
            <span aria-hidden="true">·</span>
            <span>RC {BRAND_DETAILS.rcNumber}</span>
            <span aria-hidden="true">·</span>
            <span>VAT {BRAND_DETAILS.vatNumber}</span>
          </div>
        </div>

        <div style={{
          marginTop: 18,
          fontSize: "clamp(12px, 2.8vw, 13px)",
          color: "var(--text-muted)",
          letterSpacing: 1,
          textTransform: "uppercase",
          textAlign: "center",
          lineHeight: 1.6,
        }}>
          Powered by our AI design engine   ·   Crafted with ❤ in Lagos & San Francisco
        </div>
      </div>
    </footer>
  );
}

/**
 * Update once you have real numbers / address. Kept centralised so we have
 * a single source of truth instead of business details scattered across
 * the codebase.
 */
const BRAND_DETAILS = {
  legalName:     "Oge Eyesthetic Nail Studio Ltd",
  email:         "hello@ogeeyesthetics.com",
  phoneTel:      "+12345678901",
  phoneDisplay:  "+1 (234) 567-8901",
  address:       "Lagos, Nigeria",
  rcNumber:      "Pending",
  vatNumber:     "Pending",
};

function FooterColumn({ title, links }) {
  return (
    <div>
      <h3 style={{
        fontSize: 11, fontWeight: 800, letterSpacing: 2,
        textTransform: "uppercase",
        color: "var(--text-muted)",
        marginBottom: 14,
      }}>{title}</h3>
      <ul style={{
        listStyle: "none", padding: 0, margin: 0,
        display: "grid", gap: 10,
      }}>
        {links.map((l) => (
          <li key={l.to || l.href || l.label}>
            {l.to ? (
              <Link to={l.to} style={linkStyle}>{l.label}</Link>
            ) : (
              <a href={l.href} style={linkStyle}>{l.label}</a>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function Stat({ value, label }) {
  return (
    <div style={{ padding: "0 12px" }}>
      <div style={{
        fontFamily: "var(--font-display)",
        fontSize: 26, fontWeight: 800,
        color: "var(--text)",
        letterSpacing: 0,
      }}>
        {value}
      </div>
      <div style={{
        fontSize: 11, fontWeight: 700,
        letterSpacing: 1.2, textTransform: "uppercase",
        color: "var(--text-muted)",
        marginTop: 6,
      }}>
        {label}
      </div>
    </div>
  );
}

function SocialIcon({ href, label, path }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      aria-label={label}
      style={{
        width: 36, height: 36, borderRadius: "50%",
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        background: "var(--bg-elevated)",
        color: "var(--text-muted)",
        border: "1px solid var(--border)",
        transition: "color .2s, transform .2s var(--ease-spring)",
      }}
      onMouseEnter={(e) => { e.currentTarget.style.color = "var(--accent)"; e.currentTarget.style.transform = "translateY(-2px)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.color = "var(--text-muted)"; e.currentTarget.style.transform = ""; }}
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d={path} />
      </svg>
    </a>
  );
}

const linkStyle = {
  color: "var(--text-soft)",
  textDecoration: "none",
  fontSize: "clamp(14px, 3.2vw, 15px)",
  fontWeight: 500,
  transition: "color .2s",
};
